/**
 * FamiliarisationView - Full-screen vessel familiarisation workflow
 * WBC3 Section 12.4 compliant with conditional logic for vessel types and crew ranks
 */
import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Check, Info } from 'lucide-react';
// FT-357: shell-level back handler — bottom bar owns Back. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import { useVessel } from 'contexts/VesselContext';
// import { useVesselCollections } from '../../../../hooks/useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { createBaseRecord } from '../../../../types/core';
import { generateFamiliarisationWithCustomItems } from '../../../../utils/familiarisationHelpers';
import { useStorageState } from '../../../../hooks/useStorageState';
import { useMainLogbook } from '../../../../hooks/domain/operations/useMainLogbook';
import FamiliarisationSectionContent from './FamiliarisationSectionContent';
import FamiliarisationSignOff from './FamiliarisationSignOff';
import FamiliarisationNavigation from './FamiliarisationNavigation';
import FamiliarisationNADialog from './FamiliarisationNADialog';
import SignatureModal from '../../../shared/SignatureModal';
import { useSwipeBack, SwipeIndicator } from '../../../../hooks/useSwipeBack';

const FamiliarisationView = ({ onBack, crew, onComplete, viewMode = false, initialRecord = null, template = null }) => {
  // FT-357: register onBack with the shell-level MobileBottomBar.
  useSetBackHandler(onBack);

  // Add swipe-to-go-back functionality
  const { containerRef, swipeProgress } = useSwipeBack(onBack);
  const { currentVesselId, getCurrentVessel } = useVessel()
  const currentVessel = getCurrentVessel();
  const { logFamiliarisation } = useMainLogbook();
  // const { vesselId, collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration
  const vesselId = currentVesselId; // Use currentVesselId from VesselContext
  const { value: customItems = [] } = useStorageState('customFamiliarisationItems', []);
  const [completedTopics, setCompletedTopics] = useState(initialRecord?.completedTopics || {});
  const [completedChecklists, setCompletedChecklists] = useState(initialRecord?.completedChecklists || {});
  const [topicNotes, setTopicNotes] = useState(initialRecord?.topicNotes || {});
  const [currentStep, setCurrentStep] = useState(0);
  const [familiarisationComplete, setFamiliarisationComplete] = useState(false);
  const [crewSignature, setCrewSignature] = useState('');
  const [assessorSignature, setAssessorSignature] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [crewPosition, setCrewPosition] = useState('');
  const [assessorPosition, setAssessorPosition] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showNADialog, setShowNADialog] = useState({ show: false, item: null, topicId: null });
  const [selectedNAReason, setSelectedNAReason] = useState('');
  const [paperMode, setPaperMode] = useState(false);
  const [signatureModal, setSignatureModal] = useState({ isOpen: false, type: 'crew' });
  // Paper mode specific fields
  const [paperCompletionDate, setPaperCompletionDate] = useState('');
  const [paperChecklistFile, setPaperChecklistFile] = useState(null);
  const [paperNotes, setPaperNotes] = useState('');

  // FT-19: Draft progress save + sync
  const { value: draftProgress = [], setValue: setDraftProgress } = useStorageState('familiarisation_progress', []);
  const [saveStatus, setSaveStatus] = useState(''); // 'saving', 'saved', ''
  const [draftLoaded, setDraftLoaded] = useState(false);

  // FT-19: Load draft on mount (if not in view mode)
  useEffect(() => {
    if (viewMode || initialRecord || draftLoaded) return;
    if (!crew?.id) return;

    const existingDraft = draftProgress.find(d => d.crewId === crew.id);
    if (existingDraft) {
      console.log('[FamiliarisationView] Restoring draft progress for', crew.id);
      setCompletedTopics(existingDraft.completedTopics || {});
      setCompletedChecklists(existingDraft.completedChecklists || {});
      setTopicNotes(existingDraft.topicNotes || {});
      setCurrentStep(existingDraft.currentStep || 0);
      if (existingDraft.crewSignature) setCrewSignature(existingDraft.crewSignature);
      if (existingDraft.assessorSignature) setAssessorSignature(existingDraft.assessorSignature);
      if (existingDraft.crewPosition) setCrewPosition(existingDraft.crewPosition);
      if (existingDraft.assessorPosition) setAssessorPosition(existingDraft.assessorPosition);
      if (existingDraft.completionDate) setCompletionDate(existingDraft.completionDate);
      if (existingDraft.paperMode) setPaperMode(existingDraft.paperMode);
    }
    setDraftLoaded(true);
  }, [crew?.id, draftProgress, viewMode, initialRecord, draftLoaded]);

  // FT-19: Auto-save draft on state changes (debounced)
  useEffect(() => {
    if (viewMode || initialRecord) return;
    if (!crew?.id || !draftLoaded) return;

    // Debounce save
    setSaveStatus('saving');
    const timeoutId = setTimeout(() => {
      const draft = {
        id: `draft_${crew.id}`,
        crewId: crew.id,
        status: 'in_progress',
        completedTopics,
        completedChecklists,
        topicNotes,
        currentStep,
        crewSignature,
        assessorSignature,
        crewPosition,
        assessorPosition,
        completionDate,
        paperMode,
        checklistId: template?.id || null,
        lastSaved: new Date().toISOString()
      };

      // Update draft array (upsert by crewId)
      setDraftProgress(prev => {
        const existing = prev.findIndex(d => d.crewId === crew.id);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = draft;
          return updated;
        }
        return [...prev, draft];
      });

      setSaveStatus('saved');
      // Clear "Saved" indicator after 2 seconds
      setTimeout(() => setSaveStatus(''), 2000);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [completedTopics, completedChecklists, topicNotes, currentStep, crewSignature, assessorSignature, crewPosition, assessorPosition, completionDate, paperMode, viewMode, initialRecord, crew?.id, template?.id, draftLoaded, setDraftProgress]);

  // FT-19: Delete draft when familiarisation completes (called from onComplete handler)
  const deleteDraft = () => {
    if (!crew?.id) return;
    setDraftProgress(prev => prev.filter(d => d.crewId !== crew.id));
    console.log('[FamiliarisationView] Draft deleted for', crew.id);
  };

  const vesselChecklists = useMemo(() => {
    if (!currentVessel || !crew) return {};

    try {
      // If template provided (Phase 3+), use template sections
      if (template?.sections) {
        const checklists = {};
        template.sections.forEach(section => {
          checklists[section.id] = {
            section: section.id,
            title: section.name,
            items: section.items?.map(item => ({
              id: item.id,
              topic: item.text,
              text: item.text,
              required: item.required,
              regulation: item.regulation,
              isCustom: item.isCustom
            })) || []
          };
        });
        return checklists;
      }

      // Fallback to legacy system (for viewing old records or no template)
      const familiarisationData = generateFamiliarisationWithCustomItems(currentVessel, crew, customItems);
      const checklists = {};
      if (familiarisationData && familiarisationData.sections) {
        familiarisationData.sections.forEach(section => {
          checklists[section.section] = {
            ...section,
            items: Array.isArray(section.items) ? section.items :
                   (section.topics || section.checklist || [])
          };
        });
      }
      return checklists;
    } catch (error) {
      console.error('Failed to generate checklists:', error);
      return {};
    }
  }, [currentVessel, crew, customItems, template]);

  const steps = Object.keys(vesselChecklists);
  const currentChecklist = vesselChecklists[steps[currentStep]];

  useEffect(() => {
    if (crew) {
      setCrewPosition(crew.position || crew.role || '');
    }
  }, [crew]);

  const handleItemComplete = (sectionId, itemId, status) => {
    if (status === null) {
      // Remove the item from completed checklists
      setCompletedChecklists(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
    } else {
      // Add or update the item status
      setCompletedChecklists(prev => ({
        ...prev,
        [itemId]: { status, timestamp: new Date().toISOString() }
      }));
    }
    setHasUnsavedChanges(true);
  };

  const handleTopicComplete = (topicId, isCompleted) => {
    setCompletedTopics(prev => ({
      ...prev,
      [topicId]: isCompleted
    }));
    setHasUnsavedChanges(true);
  };

  const handleNotesChange = (topicId, notes) => {
    setTopicNotes(prev => ({
      ...prev,
      [topicId]: notes
    }));
    setHasUnsavedChanges(true);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else if (!familiarisationComplete) {
      setFamiliarisationComplete(true);
    }
  };

  const handlePrevious = () => {
    if (familiarisationComplete) {
      setFamiliarisationComplete(false);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Signature modal handlers
  const handleOpenSignatureModal = (type) => {
    setSignatureModal({ isOpen: true, type });
  };

  const handleSaveSignature = (signatureData, signatureType) => {
    // signatureType is 'individual' (crew) or 'authorized' (assessor)
    if (signatureType === 'individual') {
      setCrewSignature(signatureData);
      setCrewPosition(signatureData.position || '');
    } else if (signatureType === 'authorized') {
      setAssessorSignature(signatureData);
      setAssessorPosition(signatureData.position || '');
    }
    setSignatureModal({ isOpen: false, type: 'crew' });
    setHasUnsavedChanges(true);
  };

  const handleSubmit = async () => {
    // Paper mode validation
    if (paperMode && (!paperCompletionDate || !paperChecklistFile)) {
      alert('Please provide the paper completion date and upload the scanned checklist.');
      return;
    }

    if (!crewSignature || !assessorSignature || !completionDate) {
      alert('Please complete all signature fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const completionData = {
        id: `fam_${crew.id}_${Date.now()}`,
        status: 'complete',
        crewId: crew.id,
        crewName: crew.full_name || crew.name,
        crewPosition: crewPosition,
        // Phase 3: Template info
        checklistId: template?.id || 'legacy_default',
        checklistName: template?.name || 'Vessel Familiarisation',
        checklistValidityMonths: template?.validityPeriodMonths ?? 12,
        vesselName: currentVessel?.vesselName || currentVessel?.name || 'Unknown Vessel',
        vesselType: currentVessel?.vesselType || currentVessel?.type || 'Unknown Type',
        category: currentVessel?.category || 'Unknown',
        completedTopics,
        completedChecklists,
        topicNotes,
        naReasons: selectedNAReason,
        // Signature data in signOffData object for PDF export compatibility
        signOffData: {
          crewSignature,
          assessorSignature,
          crewPosition,
          assessorPosition,
          completionDate
        },
        // Also keep at root level for backward compatibility
        crewSignature,
        assessorSignature,
        assessorPosition,
        completionDate,
        completedAt: new Date().toISOString(),
        customItems: customItems.filter(item => completedChecklists[`custom-${item.id}`]),
        // Save the checklist structure for PDF export
        vesselChecklists: vesselChecklists,
        // Paper mode data
        paperMode: paperMode,
        paperModeData: paperMode ? {
          paperCompletionDate,
          paperChecklistFile: paperChecklistFile ? {
            fileName: paperChecklistFile.fileName,
            fileType: paperChecklistFile.fileType,
            size: paperChecklistFile.size,
            fileData: paperChecklistFile.fileData,
            uploadedAt: paperChecklistFile.uploadedAt
          } : null,
          paperNotes: paperNotes || null
        } : null
      };

      onComplete(completionData);

      // FT-19: Delete draft on successful completion
      deleteDraft();

      // Log familiarisation completion to Official Logbook
      await logFamiliarisation({
        crewId: crew.id,
        crewName: crew.full_name || crew.name,
        role: crewPosition,
        checklistId: template?.id || 'vessel_familiarisation',
        checklistName: template?.name || 'Vessel Familiarisation',
        itemsCompleted: Object.keys(completedChecklists).length,
        completedBy: assessorSignature,
        completedAt: completionDate,
        timestamp: new Date().toISOString()
      });

      setHasUnsavedChanges(false);
      onBack();
    } catch (error) {
      alert('Failed to save familiarisation record. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleItemNA = (sectionId, item) => {
    setShowNADialog({ show: true, item, sectionId });
  };

  const confirmNA = (reason) => {
    const { item, sectionId } = showNADialog;
    const itemId = item.id;
    
    setCompletedChecklists(prev => ({
      ...prev,
      [itemId]: { status: 'na', reason, timestamp: new Date().toISOString() }
    }));
    
    setSelectedNAReason(prev => ({
      ...prev,
      [itemId]: reason
    }));
    
    setShowNADialog({ show: false, item: null, sectionId: null });
    setHasUnsavedChanges(true);
  };

  const getProgress = () => {
    let totalItems = 0;
    let completedItems = 0;

    Object.values(vesselChecklists).forEach(checklist => {
      // Count only the checklist items in this section
      if (checklist.items && checklist.items.length > 0) {
        checklist.items.forEach((item, index) => {
          const itemId = item.id || `${checklist.section}-item-${index}`;
          totalItems++;
          const itemStatus = completedChecklists[itemId];
          if (itemStatus && (itemStatus.status === 'completed' || itemStatus.status === 'na')) {
            completedItems++;
          }
        });
      }
      // Note: Removed section-level counting - only checklist items matter
    });

    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };

  const progress = getProgress();

  return (
    <div className="min-h-screen bg-gray-50" ref={containerRef}>
      {/* Swipe Indicator */}
      <SwipeIndicator progress={swipeProgress} />
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* FT-357: header BackButton removed — bottom bar owns Back. */}
              <div>
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {viewMode ? 'View' : 'Complete'} Vessel Familiarisation
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  {crew?.full_name || crew?.name} - {crew?.position || 'Crew Member'}
                  {/* FT-19: Draft save status indicator */}
                  {!viewMode && saveStatus && (
                    <span className={`ml-2 text-xs ${saveStatus === 'saving' ? 'text-amber-600' : 'text-green-600'}`}>
                      {saveStatus === 'saving' ? 'Saving...' : 'Saved'}
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            {/* Progress indicator */}
            <div className="flex items-center gap-4">
              <div className="hidden sm:block">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{progress}% Complete</span>
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>
              
              {/* Paper mode toggle */}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={paperMode}
                  onChange={(e) => setPaperMode(e.target.checked)}
                  className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                  disabled={viewMode}
                />
                <span className="text-sm text-gray-700">Paper mode</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* WBC3 Customization Notice Banner */}
        {!viewMode && (
          <div className="mb-6 rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <div className="flex items-center gap-2">
                <Info className="h-5 w-5 text-white" />
                <h3 className="text-base font-semibold text-white">WBC3 Compliance Notice</h3>
              </div>
            </div>
            <div className="px-6 py-5">
              <div className="text-sm text-gray-700">
                <p>
                  This familiarisation checklist is a <strong>WBC3-compliant baseline</strong>.
                  However, WBC3 Section 28.1.6 requires you to customize this checklist for your
                  specific vessel, its equipment, and your SMS.
                </p>
                <div className="mt-3">
                  <p className="font-medium text-gray-900">What this means:</p>
                  <ul className="mt-2 list-disc list-inside space-y-1 text-gray-600">
                    <li>Add vessel-specific items (e.g., unique equipment, operational procedures)</li>
                    <li>Remove items not applicable to your vessel type</li>
                    <li>Adapt item descriptions to match your vessel's systems</li>
                    <li>Document customizations in your SMS</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        {(!familiarisationComplete && !paperMode) ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Section Navigation */}
            <FamiliarisationNavigation
              sections={steps.map((step, index) => ({
                id: step,
                title: vesselChecklists[step]?.title || step,
                active: index === currentStep
              }))}
              currentStep={currentStep}
              familiarisationComplete={familiarisationComplete}
              isTopicComplete={(topicId) => completedTopics[topicId]}
              onGoToTopic={(index) => setCurrentStep(index)}
            />

            {/* Section Content */}
            <div className="p-6">
              {currentChecklist && (
                <FamiliarisationSectionContent
                  section={currentChecklist}
                  checklistData={currentChecklist}
                  completedItems={completedChecklists}
                  isTopicCompleted={completedTopics[currentChecklist?.section]}
                  notes={topicNotes[currentChecklist?.section]}
                  onItemStatusChange={handleItemComplete}
                  onTopicComplete={handleTopicComplete}
                  onNotesChange={handleNotesChange}
                  onNAClick={handleItemNA}
                  viewMode={viewMode}
                  selectedNAReason={selectedNAReason}
                  paperMode={paperMode}
                />
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md ${
                  currentStep === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </button>

              <div className="text-sm text-gray-500">
                Step {currentStep + 1} of {steps.length}
              </div>

              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
              >
                {currentStep < steps.length - 1 ? 'Next' : 'Complete'}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <FamiliarisationSignOff
                crewName={crew?.full_name || crew?.name}
                crewPosition={crewPosition}
                onCrewPosition={setCrewPosition}
                crewSignature={crewSignature}
                onCrewSignature={setCrewSignature}
                assessorSignature={assessorSignature}
                onAssessorSignature={setAssessorSignature}
                assessorPosition={assessorPosition}
                onAssessorPosition={setAssessorPosition}
                completionDate={completionDate}
                onCompletionDate={setCompletionDate}
                familiarisationComplete={familiarisationComplete}
                onFamiliarisationComplete={setFamiliarisationComplete}
                allTopicsCompleted={progress >= 100 || paperMode}
                onOpenSignatureModal={handleOpenSignatureModal}
                viewMode={viewMode}
                paperMode={paperMode}
                paperCompletionDate={paperCompletionDate}
                onPaperCompletionDate={setPaperCompletionDate}
                paperChecklistFile={paperChecklistFile}
                onPaperChecklistFile={setPaperChecklistFile}
                paperNotes={paperNotes}
                onPaperNotes={setPaperNotes}
              />
            </div>

            {/* Final Actions */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
              <button
                onClick={handlePrevious}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to Review
              </button>

              {!viewMode && (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !crewSignature || !assessorSignature || !completionDate}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-md ${
                    isSubmitting || !crewSignature || !assessorSignature || !completionDate
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-cyan-600 hover:bg-cyan-700'
                  }`}
                >
                  <Check className="h-4 w-4" />
                  {isSubmitting ? 'Saving...' : 'Submit Familiarisation'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* N/A Dialog */}
      <FamiliarisationNADialog
        show={showNADialog.show}
        item={showNADialog.item}
        onConfirm={confirmNA}
        onCancel={() => setShowNADialog({ show: false, item: null, topicId: null })}
      />

      {/* Crew Signature Modal */}
      <SignatureModal
        isOpen={signatureModal.isOpen && signatureModal.type === 'crew'}
        onClose={() => setSignatureModal({ isOpen: false, type: 'crew' })}
        onSave={handleSaveSignature}
        signatureType="individual"
        title="Crew Member Signature"
        description="As the crew member, please sign to confirm you have completed the familiarisation."
        existingSignature={typeof crewSignature === 'object' ? crewSignature.data : null}
        existingName={typeof crewSignature === 'object' ? crewSignature.name : crewSignature}
        existingPosition={crewPosition}
      />

      {/* Assessor Signature Modal */}
      <SignatureModal
        isOpen={signatureModal.isOpen && signatureModal.type === 'assessor'}
        onClose={() => setSignatureModal({ isOpen: false, type: 'assessor' })}
        onSave={handleSaveSignature}
        signatureType="authorized"
        title="Assessor Signature"
        description="As the assessor (Master/Supervisor), please sign to certify the familiarisation is complete."
        existingSignature={typeof assessorSignature === 'object' ? assessorSignature.data : null}
        existingName={typeof assessorSignature === 'object' ? assessorSignature.name : assessorSignature}
        existingPosition={assessorPosition}
      />
    </div>
  );
};

export default FamiliarisationView;